import { useState, useEffect, useContext } from 'react';
import { Context } from '../../context/UserContext';
import useFlashMessage from '../../hooks/useFlashMessage';
import styles from './styles.module.css';
import Input from '../../componentes/form/Input';
import RoundedImage from '../../layout/RoundedImage';
import DeleteAccountModal from '../../componentes/DeleteAccountModal';
import { formatCNPJ, formatPhone, formatCEP } from '../../utils/formatters';
import { MapPin, Loader2 } from 'lucide-react';

function Profile() {
  const { user, setUser, fetchUser, editUser } = useContext(Context);
  const { setFlashMessage } = useFlashMessage();
  const [localUser, setLocalUser] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');

  /* ── Upload de imagem ── */
  function onFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(file);
    setPreviewUrl(URL.createObjectURL(file));
    setLocalUser(prev => ({ ...prev, [e.target.name]: file }));
  }

  /* ── Excluir conta ── */
  async function deleteAccount(password) {
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_API}/users/delete-account`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${JSON.parse(token)}`,
      },
      body: JSON.stringify({ password }),
    });
    const data = await response.json();
    if (!response.ok) { setFlashMessage(data.message, 'error'); return; }
    localStorage.removeItem('token');
    window.location.href = '/';
  }

  /* ── ViaCEP ── */
  async function fetchCep(digits) {
    setCepLoading(true);
    setCepError('');
    try {
      const res  = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data.erro) { setCepError('CEP não encontrado.'); return; }
      setLocalUser(prev => ({
        ...prev,
        localization: {
          ...prev.localization,
          cep:     formatCEP(digits),
          address: data.logradouro || prev.localization?.address || '',
          city:    data.localidade  || prev.localization?.city    || '',
          state:   data.uf          || prev.localization?.state   || '',
        },
      }));
    } catch {
      setCepError('Erro ao buscar CEP. Preencha manualmente.');
    } finally {
      setCepLoading(false);
    }
  }

  /* ── handleChange com formatters ── */
  function handleChange(e) {
    const { name, value } = e.target;

    // Campos de localização (dot notation: "localization.cep")
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      let formatted = value;
      if (child === 'cep') {
        formatted = formatCEP(value);
        const digits = value.replace(/\D/g, '');
        if (digits.length === 8) fetchCep(digits);
        else setCepError('');
      }
      setLocalUser(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: formatted },
      }));
      return;
    }

    // Campos simples com formatters
    let formatted = value;
    if (name === 'phone') formatted = formatPhone(value);
    if (name === 'cnpj')  formatted = formatCNPJ(value);

    setLocalUser(prev => ({ ...prev, [name]: formatted }));
  }

  /* ── Submit ── */
  async function handleSubmit(e) {
    e.preventDefault();
    if (!localUser) return;
    const updated = await editUser(localUser, preview);
    if (updated) {
      setUser(updated);
      setPreview(null);
      setPreviewUrl(null);
      setFlashMessage('Perfil atualizado com sucesso!', 'success');
    }
  }

  /* ── Carrega usuário ── */
  useEffect(() => {
    if (!user) {
      fetchUser();
    } else {
      setLocalUser({
        ...user,
        localization: user.localization || { address: '', city: '', state: '', cep: '' },
      });
    }
  }, [user, fetchUser]);

  // Libera a URL temporária do preview quando o componente desmonta
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  if (!localUser) return <p className={styles.loading}>Carregando...</p>;

  // ── Resolução da imagem ──────────────────────────────────────────
  // previewUrl  → arquivo recém-selecionado (ainda não salvo)
  // user.image  → URL completa do Cloudinary (https://res.cloudinary.com/...)
  //               NÃO precisa mais de REACT_APP_API + path local
  const imageSrc = previewUrl || user.image || null;

  return (
    <section className={styles.profile_container}>

      {/* Header com foto */}
      <div className={styles.profile_header}>
        <h1>Configurações de Perfil</h1>
        {imageSrc && (
          <RoundedImage src={imageSrc} alt={user.name} />
        )}
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>

        {/* ── Foto ── */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Foto de Perfil</p>
          <Input text="Imagem" type="file" name="image" handleOnChange={onFileChange} />
        </div>

        {/* ── Dados pessoais ── */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Dados Pessoais</p>
          <div className={styles.row}>
            <Input text="Nome" type="text" name="name" placeholder="Digite seu nome"
              handleOnChange={handleChange} value={localUser.name || ''} />
            <Input text="Email" type="email" name="email" placeholder="Digite seu email"
              handleOnChange={handleChange} value={localUser.email || ''} />
          </div>
          <div className={styles.row}>
            <Input text="Telefone" type="text" name="phone" placeholder="(11) 99999-9999"
              handleOnChange={handleChange} value={localUser.phone || ''} />
            <Input text="CNPJ" type="text" name="cnpj" placeholder="00.000.000/0000-00"
              handleOnChange={handleChange} value={localUser.cnpj || ''} />
          </div>
        </div>

        {/* ── Localização ── */}
        <div className={styles.locationBox}>
          <div className={styles.locationHeader}>
            <MapPin size={14} className={styles.locationIcon} />
            <span className={styles.locationTitle}>Localização</span>
          </div>

          {/* CEP com busca automática */}
          <div className={styles.cepWrap}>
            <div className={styles.cepInputWrap}>
              <Input
                text="CEP"
                type="text"
                name="localization.cep"
                placeholder="00000-000"
                handleOnChange={handleChange}
                value={localUser.localization?.cep || ''}
              />
              {cepLoading && <Loader2 size={15} className={styles.cepSpinner} />}
            </div>
            {cepError && <p className={styles.cepError}>{cepError}</p>}
          </div>

          {/* Endereço preenchido pelo ViaCEP ou manualmente */}
          <Input
            text="Endereço"
            type="text"
            name="localization.address"
            placeholder="Rua, número, bairro"
            handleOnChange={handleChange}
            value={localUser.localization?.address || ''}
          />

          {/* Cidade + UF */}
          <div className={styles.cityStateRow}>
            <div className={styles.cityField}>
              <Input
                text="Cidade"
                type="text"
                name="localization.city"
                placeholder="São Paulo"
                handleOnChange={handleChange}
                value={localUser.localization?.city || ''}
              />
            </div>
            <div className={styles.stateField}>
              <Input
                text="UF"
                type="text"
                name="localization.state"
                placeholder="SP"
                handleOnChange={handleChange}
                value={localUser.localization?.state || ''}
              />
            </div>
          </div>
        </div>

        {/* ── Senha ── */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Alterar Senha <span className={styles.optional}>(opcional)</span></p>
          <div className={styles.row}>
            <Input text="Nova senha" type="password" name="password"
              placeholder="Deixe vazio para manter" handleOnChange={handleChange} />
            <Input text="Confirmar senha" type="password" name="confirmpassword"
              placeholder="Repita a nova senha" handleOnChange={handleChange} />
          </div>
        </div>

        {/* ── Ações ── */}
        <div className={styles.actions}>
          <input type="submit" value="Salvar alterações" className={styles.save_btn} />
          <button type="button" onClick={() => setShowDeleteModal(true)} className={styles.delete_btn}>
            Excluir conta
          </button>
        </div>
      </form>

      {showDeleteModal && (
        <DeleteAccountModal
          onConfirm={deleteAccount}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </section>
  );
}

export default Profile;